import {useEffect, useState, useCallback} from 'react';
import {Banner, Button, Icon, TextField, TextArea, view} from '@sail/ui';
import {warning} from '@sail/icons/react/Icon';
import {defineMessages, FormattedMessage, useIntl} from 'react-intl';
import {useTheme} from '../ThemeProvider';
import {SendUsAnEmailConfirmation} from './SendUsAnEmailConfirmation';
import {SendUsAnEmailCloseButton} from './SendUsAnEmailCloseButton';
import {SendUsAnEmailSubmitButton} from './SendUsAnEmailSubmitButton';
import {
  DEFAULT_SUPPORTED_MIME_TYPES,
  useFileUploader,
} from '../FileUploaderProvider';
import {AttachmentUploadButton} from './Attachments/AttachmentUploadButton';
import {useDragAndDrop} from '../helpers/useDragAndDrop';
import {DropFileZone} from './Attachments/DropFileZone';
import {AttachmentPreviewList} from './Attachments/AttachmentPreviewList';
import {Attachment} from './Attachments/Attachment';

export const messages = defineMessages({
  formTitle: {
    id: 'conversations_ui.sendUsAnEmailForm.title',
    description: 'Title shown in the send us an email form',
    defaultMessage: 'Send us an email',
  },
  fileUploaderDescription: {
    id: 'conversations_ui.sendUsAnEmailForm.attachments.description',
    description:
      'Describes what kind of attachments are supported when adding a new attachment',
    defaultMessage:
      'You may upload jpeg, png, pdf, xml, csv, tsv, xlsx, or docx files that are less than 20 MB each.',
  },
  formIssueDescriptionLabel: {
    id: 'conversations_ui.sendUsAnEmailForm.formIssueDescriptionLabel',
    description: 'Label for issue description in support widget contact form',
    defaultMessage: 'Provide details about what you need help with',
  },
  formIssueDescriptionPlaceholder: {
    id: 'conversations_ui.sendUsAnEmailForm.formIssueDescriptionPlaceholder',
    description:
      'Placeholder for issue description in support widget contact form',
    defaultMessage: 'Give us as much information as you can',
  },
  formSubmitButtonText: {
    id: 'conversations_ui.sendUsAnEmailForm.formSubmitButtonText',
    description: 'Submit button text in support widget contact form',
    defaultMessage: 'Send',
  },
  ccEmailsPlaceholder: {
    id: 'conversations_ui.sendUsAnEmailForm.ccEmails.placeholder',
    description: 'Placeholder for optional input CC email addresses',
    defaultMessage: 'company@example.com, person@example.com',
  },
  ccEmailsLabel: {
    id: 'conversations_ui.sendUsAnEmailForm.ccEmails.label',
    description: 'Label for optional input CC email addresses',
    defaultMessage: 'CC additional emails (optional)',
  },
  ccEmailsError: {
    id: 'conversations_ui.sendUsAnEmailForm.ccEmails.error',
    description: 'Error message for optional input CC email addresses',
    defaultMessage: 'Please enter valid email addresses.',
  },
  formSubmissionError: {
    id: 'conversations_ui.sendUsAnEmailForm.formSubmissionError',
    description: 'Text shown when there was an error submitting the form',
    defaultMessage:
      'An unexpected error occurred when submitting your support request. We have saved your input.',
  },
  rateLimitedError: {
    id: 'conversations_ui.sendUsAnEmailForm.rateLimitedError',
    description: 'Text shown when the user has submitted too many requests',
    defaultMessage:
      'You have submitted too many requests. Please try again later.',
  },
  emailSupportLabel: {
    id: 'conversations_ui.sendUsAnEmailForm.emailSupportLabel',
    description:
      'Label for the email support option in the send us an email form',
    defaultMessage: 'Contact Support',
  },
  unauthenticatedEmailPlaceholder: {
    id: 'conversations_ui.sendUsAnEmailForm.unauthenticatedEmail.placeholder',
    description:
      'Placeholder for optional email address input when not logged in',
    defaultMessage: 'you@example.com',
  },
  unauthenticatedEmailLabel: {
    id: 'conversations_ui.sendUsAnEmailForm.unauthenticatedEmail.label',
    description: 'Label for optional email address input when not logged in',
    defaultMessage: 'Your email',
  },
  unauthenticatedEmailError: {
    id: 'conversations_ui.sendUsAnEmailForm.unauthenticatedEmail.error',
    description:
      'Error message for optional email address input when not logged in',
    defaultMessage: 'Please enter a valid email address for us to contact you.',
  },
  subjectFieldLabel: {
    id: 'conversations_ui.sendUsAnEmailForm.subjectField.label',
    description: 'Label for subject field in support widget contact form',
    defaultMessage: 'Email subject (optional)',
  },
  subjectFieldPlaceholder: {
    id: 'conversations_ui.sendUsAnEmailForm.subjectField.placeholder',
    description: 'Placeholder for subject field in support widget contact form',
    defaultMessage: 'Ex. Requesting account transfer',
  },
});

export const fileUploadMessages = defineMessages({
  fileTooBigNotice: {
    id: 'conversations_ui.attachments.fileTooBigNotice',
    description: 'Message indicating that the selected file is too big.',
    defaultMessage:
      'File is too large, please upload a file smaller than 20 MB',
  },
  fileInvalidNotice: {
    id: 'conversations_ui.attachments.fileInvalidNotice',
    description:
      'Message indicating that the selected file is invalid or corrupted.',
    defaultMessage:
      'There appears to be an issue with the selected file, please check it and try again',
  },
  fileTypeNotSupportedNotice: {
    id: 'conversations_ui.attachments.fileTypeNotSupportedNotice',
    description: 'Message indicating that this type of file is not supported.',
    defaultMessage:
      'File type is not supported, please upload a jpeg, png, pdf, xml, csv, tsv, xlsx, or docx file',
  },
  tooManyFilesNotice: {
    id: 'conversations_ui.attachments.tooManyFilesNotice',
    description:
      'Message indicating that the user tried to upload too many files.',
    defaultMessage: 'A maximum of 5 files can be uploaded',
  },
  unexpectedErrorNotice: {
    id: 'conversations_ui.attachments.unexpectedErrorNotice',
    description:
      'Message indicating that the file failed to upload for unknown reasons.',
    defaultMessage: 'File could not be uploaded, please try again later',
  },
  batchUploadErrorNotice: {
    id: 'conversations_ui.attachments.batchUploadErrorNotice',
    description: 'Message indicating that some files failed to upload.',
    defaultMessage:
      'There was an error uploading one of your files: {errorMessage}',
  },
  fileNameTooLongNotice: {
    id: 'conversations_ui.attachments.fileNameTooLongNotice',
    description:
      'Message indicating that the file cannot be uploaded because the filename is too long.',
    defaultMessage: 'File name must be 200 characters or less',
  },
});

const getMessageForUploadError = (error: any) => {
  const errorCode = error?.code || null;

  switch (errorCode) {
    case 'file_too_large':
      return fileUploadMessages.fileTooBigNotice;
    case 'upload_type_not_supported':
      return fileUploadMessages.fileTypeNotSupportedNotice;
    case 'upload_ext_invalid':
      return fileUploadMessages.fileInvalidNotice;
    case 'upload_not_supported':
      return fileUploadMessages.fileInvalidNotice;
    case 'too_many_files':
      return fileUploadMessages.tooManyFilesNotice;
    case 'file_name_too_long':
      return fileUploadMessages.fileNameTooLongNotice;
    default:
      return fileUploadMessages.unexpectedErrorNotice;
  }
};

export interface SendUsAnEmailFormValues {
  subject?: string;
  ccEmails?: string[];
  unauthenticatedEmail?: string;
  question?: string;
  attachments?: Attachment[];
}

export type HandleSubmitEmail = (
  values: SendUsAnEmailFormValues,
) => Promise<void>;

export interface SendUsAnEmailFormProps {
  useNewDesigns?: boolean;
  initialQuestionValue?: string;
  onChangeQuestion?: (question: string) => void;
  isRateLimitedError?: boolean;
  isSubmissionError?: boolean;
  fallbackEmailUrl?: string;
  showSubjectField?: boolean;
  showUnauthenticatedEmailField?: boolean;
  hasUnauthenticatedEmailError?: boolean;
  showCcEmailsField?: boolean;
  hasCcEmailsError?: boolean;
  existingCcEmails?: string[];
  isSubmissionPending?: boolean;
  submissionSuccess?: boolean;
  handleSubmit: HandleSubmitEmail;
  onCancel: () => void;
  onEmailConfirmationClose?: () => void;
  onFileUploadError?: (error: any) => void;
  supportedUploadMimeTypes?: string[];
}

export function SendUsAnEmailForm({
  useNewDesigns,
  initialQuestionValue,
  onChangeQuestion,
  isRateLimitedError,
  isSubmissionError,
  fallbackEmailUrl,
  showSubjectField,
  showUnauthenticatedEmailField,
  hasUnauthenticatedEmailError,
  showCcEmailsField,
  hasCcEmailsError,
  existingCcEmails,
  isSubmissionPending,
  submissionSuccess,
  handleSubmit,
  onCancel,
  onEmailConfirmationClose,
  onFileUploadError,
  supportedUploadMimeTypes,
}: SendUsAnEmailFormProps) {
  const intl = useIntl();
  const theme = useTheme();
  const {
    available: fileUploaderAvailable,
    upload,
    validateFile,
  } = useFileUploader();

  const [submitting, setSubmitting] = useState<boolean>(false);

  const [subject, setSubject] = useState<string>();

  const [unauthenticatedEmail, setUnauthenticatedEmail] = useState<
    string | undefined
  >();
  const [unauthenticatedEmailError, setUnauthenticatedEmailError] =
    useState<boolean>(Boolean(hasUnauthenticatedEmailError));

  // Tracks whether there has _ever_ been an unauthenticated email error,
  // during the lifetime of the component.
  const [
    everHadUnauthenticatedEmailError,
    setEverHadUnauthenticatedEmailError,
  ] = useState<boolean>(Boolean(hasUnauthenticatedEmailError));

  const [question, setQuestion] = useState<string | undefined>(
    initialQuestionValue,
  );

  const [ccEmails, setCcEmails] = useState<string[]>(existingCcEmails || []);
  const [ccEmailsError, setCcEmailsError] = useState<boolean>(
    Boolean(hasCcEmailsError),
  );

  const [attachments, setAttachments] = useState<Attachment[]>([]);

  const [isUploading, setIsUploading] = useState<boolean>(false);

  const [fileUploadErrorMessage, setFileUploadErrorMessage] =
    useState<React.ReactNode>(null);

  useEffect(() => {
    if (hasUnauthenticatedEmailError) {
      setUnauthenticatedEmailError(true);
      setEverHadUnauthenticatedEmailError(true);
    }
  }, [hasUnauthenticatedEmailError]);

  useEffect(() => {
    if (hasCcEmailsError) {
      setCcEmailsError(true);
    }
  }, [hasCcEmailsError]);

  const onChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const value = e.target.value || '';
      setQuestion(value);
      onChangeQuestion?.(value);
    },
    [onChangeQuestion],
  );

  const onSubmit = useCallback(async () => {
    setSubmitting(true);
    await handleSubmit({
      subject,
      ccEmails,
      unauthenticatedEmail,
      question,
      attachments,
    });
    setSubmitting(false);
  }, [
    handleSubmit,
    subject,
    ccEmails,
    unauthenticatedEmail,
    question,
    attachments,
  ]);

  const processFile = useCallback(
    async (file: File): Promise<Attachment | null> => {
      const requestAbortController = new AbortController();

      const fileId = await upload(file, {
        abortSignal: requestAbortController.signal,
      });
      const attachment: Attachment = {
        id: fileId,
        fileName: file.name,
        mimeType: file.type,
        file,
      };

      return attachment;
    },
    [upload],
  );

  const onFilesSelected = useCallback(
    async (files: FileList) => {
      setIsUploading(true);
      try {
        for (let i = 0; i < files.length; i += 1) {
          const file = files[i];
          validateFile(file);

          // Upload attachments sequentially
          // eslint-disable-next-line no-await-in-loop
          const attachment = await processFile(file);
          if (attachment) {
            setAttachments((currentAttachments) => [
              ...currentAttachments,
              attachment,
            ]);
          }
        }
      } catch (error: any) {
        onFileUploadError?.(error);
        const message = (
          <FormattedMessage {...getMessageForUploadError(error)} />
        );
        if (files.length > 1 && error?.code !== 'too_many_files') {
          setFileUploadErrorMessage(
            <FormattedMessage
              {...fileUploadMessages.batchUploadErrorNotice}
              values={{errorMessage: message}}
            />,
          );
        } else {
          setFileUploadErrorMessage(message);
        }
      } finally {
        setIsUploading(false);
      }
    },
    [validateFile, processFile, onFileUploadError],
  );

  const handleDragAndDropDataTransfer = useCallback(
    (dataTransfer: DataTransfer) => {
      if (isUploading) {
        return;
      }

      const {files} = dataTransfer;
      if (files && files.length > 0) {
        onFilesSelected(files);
      }
    },
    [isUploading, onFilesSelected],
  );

  const onRemoveAttachment = (attachment: Attachment) => {
    setAttachments((currentAttachments) =>
      currentAttachments.filter(
        (currentAttachment) => currentAttachment.id !== attachment.id,
      ),
    );
  };

  const {isDragging, handleDragLeave, handleDragOver, handleDrop} =
    useDragAndDrop({onDataTransfer: handleDragAndDropDataTransfer});

  if (submissionSuccess) {
    return (
      <SendUsAnEmailConfirmation
        useNewDesigns={useNewDesigns}
        onEmailConfirmationClose={onEmailConfirmationClose}
      />
    );
  }

  const isLinkAudience = theme.audience === 'link';
  const linkInputFocusStyles = {
    '& input:focus': {
      focusRing: '0 0 0 4px #D8DEE4',
      keyline: theme.primaryActionColor,
    },
    '& textarea:focus': {
      focusRing: '0 0 0 4px #D8DEE4',
      keyline: theme.primaryActionColor,
    },
  };

  // we will continue to use default sail focus styles for stripe and express audience
  const inputFocusStyles = isLinkAudience ? linkInputFocusStyles : undefined;

  const isPendingOrSubmitting = isSubmissionPending || submitting;

  return (
    <view.div
      data-testid="send-us-an-email-container"
      css={{position: 'relative'}}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDragEnd={handleDragLeave}
      onDrop={handleDrop}
    >
      {isDragging && fileUploaderAvailable ? <DropFileZone /> : null}

      <view.form
        onSubmit={(e) => e.preventDefault()}
        css={{
          position: 'relative',
          opacity: isDragging ? 0 : 1,
        }}
      >
        <view.div
          css={{
            font: 'heading.small',
            fontWeight: 'semibold',
            marginBottom: fileUploaderAvailable ? 'xsmall' : 'medium',
          }}
        >
          {intl.formatMessage(messages.formTitle)}
        </view.div>

        {fileUploaderAvailable ? (
          <view.div
            css={{
              font: 'label.small',
              color: 'secondary',
              marginBottom: 'medium',
            }}
          >
            {intl.formatMessage(messages.fileUploaderDescription)}
          </view.div>
        ) : null}

        {fileUploadErrorMessage ? (
          <FileUploadErrorMessage errorMessage={fileUploadErrorMessage} />
        ) : null}

        {isRateLimitedError ? <RateLimitedError /> : null}

        {isSubmissionError ? (
          <SubmissionError emailUrl={fallbackEmailUrl} />
        ) : null}

        {/* Even if the props suggest not to show the unauthenticated email field,
           we still render it if there is an error, so that the user can fix it.
           We use everHadUnauthenticatedEmailError because we clear the basic
           unauthenticatedEmailError state once the user starts typing into the field */}
        {showUnauthenticatedEmailField || everHadUnauthenticatedEmailError ? (
          <view.div css={{marginTop: 'medium', marginBottom: 'medium'}}>
            <TextField
              disabled={isPendingOrSubmitting}
              label={intl.formatMessage(messages.unauthenticatedEmailLabel)}
              placeholder={intl.formatMessage(
                messages.unauthenticatedEmailPlaceholder,
              )}
              value={unauthenticatedEmail}
              onChange={(e) => {
                setUnauthenticatedEmailError(false);
                setUnauthenticatedEmail(e.target.value.trim());
              }}
              error={
                unauthenticatedEmailError &&
                intl.formatMessage(messages.unauthenticatedEmailError)
              }
              data-testid="email-form-unauthed-email-input"
              id="unauthenticatedEmailInput"
              css={inputFocusStyles}
            />
          </view.div>
        ) : null}

        <view.div>
          <TextArea
            disabled={isPendingOrSubmitting}
            label="" // intentionally empty to match design
            data-testid="email-form-question-input"
            placeholder={intl.formatMessage(
              messages.formIssueDescriptionPlaceholder,
            )}
            defaultValue={initialQuestionValue}
            onChange={onChange}
            required
            resizeable
            rows={8}
            id="question"
            css={inputFocusStyles}
          />
        </view.div>

        {showSubjectField ? (
          <view.div css={{marginTop: 'medium'}}>
            <TextField
              disabled={isPendingOrSubmitting}
              label={intl.formatMessage(messages.subjectFieldLabel)}
              placeholder={intl.formatMessage(messages.subjectFieldPlaceholder)}
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              data-testid="email-form-subject-input"
              id="subjectInput"
              css={inputFocusStyles}
            />
          </view.div>
        ) : null}

        {showCcEmailsField ? (
          <view.div css={{marginTop: 'medium'}}>
            <TextField
              disabled={isPendingOrSubmitting}
              label={intl.formatMessage(messages.ccEmailsLabel)}
              placeholder={intl.formatMessage(messages.ccEmailsPlaceholder)}
              defaultValue={ccEmails.join(', ')}
              onChange={(e) => {
                setCcEmailsError(false);
                setCcEmails(
                  e.target.value
                    .split(/\s+|,/)
                    .filter(Boolean)
                    .map((email) => email.trim()),
                );
              }}
              error={
                ccEmailsError && intl.formatMessage(messages.ccEmailsError)
              }
              data-testid="email-form-cc-input"
              id="emailCcInput"
              css={inputFocusStyles}
            />
          </view.div>
        ) : null}

        <AttachmentPreviewList
          attachments={attachments}
          onRemoveAttachment={onRemoveAttachment}
        />

        <view.div css={{marginTop: 'medium'}}>
          <SendUsAnEmailSubmitButton
            onPress={onSubmit}
            disabled={
              !!fallbackEmailUrl ||
              isPendingOrSubmitting ||
              !question ||
              question.trim().length === 0 ||
              isUploading
            }
            pending={isPendingOrSubmitting}
            dataTestId="conversations-ui-email-form-submit-button"
            size="medium"
          >
            {intl.formatMessage(messages.formSubmitButtonText)}
          </SendUsAnEmailSubmitButton>
        </view.div>

        {fileUploaderAvailable ? (
          <view.div css={{marginTop: 'small'}}>
            <AttachmentUploadButton
              name="add-attachment"
              isLoading={isUploading}
              disabled={isPendingOrSubmitting}
              onFilesSelected={onFilesSelected}
              supportedMimeTypes={
                supportedUploadMimeTypes || DEFAULT_SUPPORTED_MIME_TYPES
              }
            />
          </view.div>
        ) : null}

        <SendUsAnEmailCloseButton
          onClick={onCancel}
          dataTestId="conversations-ui-email-form-close-button"
        />
      </view.form>
    </view.div>
  );
}

function RateLimitedError() {
  const intl = useIntl();
  return (
    <view.div css={{paddingBottom: '12px'}}>
      <Banner
        type="critical"
        description={intl.formatMessage(messages.rateLimitedError)}
      />
    </view.div>
  );
}

function SubmissionError({emailUrl}: {emailUrl?: string}) {
  const intl = useIntl();
  const cta = emailUrl ? (
    <Button href={emailUrl} target={emailUrl}>
      <FormattedMessage {...messages.emailSupportLabel} />
    </Button>
  ) : null;

  return (
    <view.div css={{paddingBottom: '12px'}}>
      <Banner
        type="critical"
        description={intl.formatMessage(messages.formSubmissionError)}
        actions={cta}
      />
    </view.div>
  );
}

function FileUploadErrorMessage({
  errorMessage,
}: {
  errorMessage: React.ReactNode;
}) {
  return (
    <view.p
      css={{
        minHeight: '$space.xlarge',
        display: 'flex',
        alignItems: 'center',
        font: 'body.small',
        color: '$color.critical500',
      }}
    >
      <>
        <Icon
          icon={warning}
          size="small"
          data-testid="warning-icon"
          css={{
            marginRight: 150,
            flexShrink: '0',
          }}
        />
        {errorMessage}
      </>
    </view.p>
  );
}
